import { useQuery } from 'react-query';

import type {
  LoadFileImportByteProgress,
  LoadFileImportCounters,
} from '../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { QueryEntity } from '../types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export type LoadFileImportProgressStatus = 'pending' | 'processing' | 'complete' | 'failed';

interface LoadFileImportStatusResponse extends LoadFileImportCounters {
  status: LoadFileImportProgressStatus;
  bytesRead?: number;
  totalBytes?: number;
}

interface UseGetLoadFileImportStatusReturn {
  counters: LoadFileImportCounters | null;
  byteProgress: LoadFileImportByteProgress | null;
  isComplete: boolean;
  error: string | null;
}

const POLL_INTERVAL_MS = 1000;

const isTerminal = (status: LoadFileImportProgressStatus | undefined) => status === 'complete' || status === 'failed';

/**
 * Polls the load file import status endpoint once a second while an import runs,
 * stopping once the job reaches a terminal state. Pass the `importId` (the status
 * route is keyed by import, not job id) once `/start` has been called.
 */
const useGetLoadFileImportStatus = (
  matterId: string | undefined,
  importId: string | undefined,
  options: { enabled?: boolean } = {},
): UseGetLoadFileImportStatusReturn => {
  const { enabled = true } = options;
  const { fetchConfigGET } = useGetFetchConfig();
  const isEnabled = enabled && !!matterId && !!importId;

  const { data, error } = useQuery<LoadFileImportStatusResponse, Error>(
    [QueryEntity.LoadFileImportStatus, { matterId, importId }],
    async () => {
      const response = await fetch(
        `${APIBaseChronos}/api/relativity/load-file-import/${importId}/status?matterId=${matterId}`,
        fetchConfigGET,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch progress: ${response.status}`);
      }

      return response.json();
    },
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchInterval: (latest) => (isTerminal(latest?.status) ? false : POLL_INTERVAL_MS),
    },
  );

  const { status, bytesRead, totalBytes, ...counters } = data ?? {};
  const isComplete = status === 'complete';

  return {
    counters: data ? (counters as LoadFileImportCounters) : null,
    byteProgress: bytesRead !== undefined && totalBytes !== undefined ? { bytesRead, totalBytes } : null,
    isComplete,
    error: status === 'failed' ? 'Import failed' : (error?.message ?? null),
  };
};

export default useGetLoadFileImportStatus;
